import {
    Cancel as CancelIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Save as SaveIcon,
    VideoLibrary as VideoLibraryIcon,
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ApplicationButton, LocalizedTextField, UploadAvatarDialog as UploadAvatarDialogJS } from '../../../../MicrospecDynamicUI/Components';
import uploadSingleFileToStorage from '../../../../MicrospecDynamicUI/Helpers/saveFileToStorage';
import { useOrganizationContext } from '../../../../MicrospecDynamicUI/Dashboard/OrganizationContext';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById, saveDocument } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessProject } from '../../../../MicrospecDynamicUI/Models/Business/BusinessProject';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { BRANCH_ID, ORG_ID, PROJECTS_COLLECTION } from '../../../constants/collectionPaths';

const UploadAvatarDialog = UploadAvatarDialogJS as any;

interface UpsertProjectProps {
  projectId?: string;
  onSave?: (project: BusinessProject) => void;
  onCancel?: () => void;
}

const UpsertProject: React.FC<UpsertProjectProps> = ({ projectId, onSave, onCancel }) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const theme = useTheme();
  const { usedLocalization } = useOrganizationContext();
  const organizationId = ORG_ID;
  const branchId = BRANCH_ID;
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDialogKey, setUploadDialogKey] = useState(0);
  const [currentStorageRef, setCurrentStorageRef] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const tempIdRef = useRef<string>(`temp_${Date.now()}`);
  const storageId = projectId || tempIdRef.current;

  const isMultiLocale = usedLocalization?.supported?.length > 1;

  const [formData, setFormData] = useState<BusinessProject>({
    name: isMultiLocale ? {} : '',
    description: isMultiLocale ? {} : '',
    mediaUrls: [],
    is_published: true,
    order: 0,
  });

  useEffect(() => {
    const load = async () => {
      if (!projectId || !db || !organizationId || !branchId) return;
      setLoading(true);
      try {
        const data = await getDocById(null, null, db, PROJECTS_COLLECTION, projectId, false);
        if (data) {
          setFormData({
            name: data.name || '',
            description: data.description || '',
            mediaUrls: data.mediaUrls || [],
            is_published: data.is_published ?? true,
            order: data.order || 0,
            created_at: data.created_at,
            updated_at: data.updated_at,
          });
        }
      } catch (err) {
        setError(translate('common.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId, db, organizationId, branchId, translate]);

  const handleInputChange = (field: keyof BusinessProject, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const makeMediaRef = (ext: string) =>
    `organizations/${organizationId}/branches/${branchId}/projects/${storageId}/media/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

  const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i.test(url);

  const openUploadDialog = () => {
    setCurrentStorageRef(makeMediaRef('jpg'));
    setUploadDialogOpen(true);
  };

  const handleImageUploaded = (url: string) => {
    setFormData(prev => ({ ...prev, mediaUrls: [...(prev.mediaUrls || []), url] }));
    setUploadDialogKey(k => k + 1);
    setCurrentStorageRef(makeMediaRef('jpg'));
    // Keep dialog open for multi-image workflow
  };

  const handleVideoFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop() || 'mp4';
    setVideoUploading(true);
    setError(null);
    try {
      const url = await uploadSingleFileToStorage(
        cloudProvider.storage,
        makeMediaRef(ext),
        file
      );
      setFormData(prev => ({ ...prev, mediaUrls: [...(prev.mediaUrls || []), url] }));
    } catch (err: any) {
      setError(err.message || translate('project.failedToSave'));
    } finally {
      setVideoUploading(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({ ...prev, mediaUrls: (prev.mediaUrls || []).filter((_, i) => i !== index) }));
  };

  const validateForm = (): boolean => {
    const name = formData.name;
    if (typeof name === 'string' && !name.trim()) {
      setError(translate('project.nameRequired'));
      return false;
    }
    if (typeof name === 'object' && !Object.values(name).some(v => v.trim())) {
      setError(translate('project.nameRequired'));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!db || !organizationId || !branchId) {
      setError(translate('common.missingContext'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const projectData: BusinessProject = {
        ...formData,
        created_at: projectId ? formData.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const result = await saveDocument(firestore, PROJECTS_COLLECTION, projectData, { id: projectId });
      setSuccess(true);
      if (onSave) {
        onSave({ ...projectData, id: result.id });
      } else {
        if (!projectId) {
          setFormData({ name: '', description: '', mediaUrls: [], is_published: true, order: 0 });
        }
        setSuccess(false);
      }
    } catch (err: any) {
      setError(err.message || translate('project.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setFormData({ name: '', description: '', mediaUrls: [], is_published: true, order: 0 });
      setError(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark'
            ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`
            : `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 3,
              color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
            }}
          >
            {projectId ? translate('project.editTitle') : translate('project.createTitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {translate('project.savedSuccessfully')}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12} md={8}>
              {isMultiLocale ? (
                <LocalizedTextField
                  languageConfig={usedLocalization}
                  value={typeof formData.name === 'object' ? formData.name : { [usedLocalization.preferred]: formData.name || '' }}
                  onChange={(value: any) => handleInputChange('name', value)}
                  label={translate('project.name')}
                  required
                  variant="outlined"
                />
              ) : (
                <TextField
                  fullWidth
                  label={translate('project.name')}
                  value={typeof formData.name === 'string' ? formData.name : ''}
                  onChange={e => handleInputChange('name', e.target.value)}
                  required
                  variant="outlined"
                />
              )}
            </Grid>

            {/* Order */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={translate('project.order')}
                type="number"
                value={formData.order ?? 0}
                onChange={e => handleInputChange('order', parseInt(e.target.value) || 0)}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              {isMultiLocale ? (
                <LocalizedTextField
                  languageConfig={usedLocalization}
                  value={typeof formData.description === 'object' ? formData.description : { [usedLocalization.preferred]: formData.description || '' }}
                  onChange={(value: any) => handleInputChange('description', value)}
                  label={translate('project.description')}
                  multiline
                  rows={3}
                  variant="outlined"
                />
              ) : (
                <TextField
                  fullWidth
                  label={translate('project.description')}
                  value={typeof formData.description === 'string' ? formData.description : ''}
                  onChange={e => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  variant="outlined"
                />
              )}
            </Grid>

            {/* Media */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {translate('project.images')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {(formData.mediaUrls || []).map((url, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 160,
                      height: 110,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      backgroundColor: theme.palette.grey[900],
                    }}
                  >
                    {isVideoUrl(url) ? (
                      <>
                        <Box
                          component="video"
                          src={url}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                          }}
                        >
                          <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 36, opacity: 0.85 }} />
                        </Box>
                      </>
                    ) : (
                      <Box
                        component="img"
                        src={url}
                        alt={`media-${index}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                    <Tooltip title={translate('common.delete')}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.85),
                          color: '#fff',
                          '&:hover': { backgroundColor: theme.palette.error.main },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}

                {/* Add image tile */}
                <Box
                  sx={{
                    width: 160,
                    height: 110,
                    borderRadius: 2,
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gap: 0.5,
                    color: alpha(theme.palette.primary.main, 0.7),
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                  onClick={openUploadDialog}
                >
                  <CloudUploadIcon />
                  <Typography variant="caption" fontWeight={500}>
                    {translate('project.addImage')}
                  </Typography>
                </Box>

                {/* Add video tile */}
                <Box
                  sx={{
                    width: 160,
                    height: 110,
                    borderRadius: 2,
                    border: `2px dashed ${alpha(theme.palette.secondary.main, 0.4)}`,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: videoUploading ? 'not-allowed' : 'pointer',
                    gap: 0.5,
                    color: alpha(theme.palette.secondary.main, 0.7),
                    '&:hover': { backgroundColor: videoUploading ? undefined : alpha(theme.palette.secondary.main, 0.08) },
                  }}
                  onClick={() => !videoUploading && videoInputRef.current?.click()}
                >
                  {videoUploading ? (
                    <CircularProgress size={24} color="secondary" />
                  ) : (
                    <VideoLibraryIcon />
                  )}
                  <Typography variant="caption" fontWeight={500}>
                    {videoUploading ? translate('common.loading') : translate('project.addVideo')}
                  </Typography>
                </Box>

                {/* Hidden video file input */}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo"
                  style={{ display: 'none' }}
                  onChange={handleVideoFileSelected}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {translate('project.imagesHelperText')}
              </Typography>
            </Grid>

            {/* Published */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published ?? true}
                    onChange={e => handleInputChange('is_published', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {translate('project.published')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(formData.is_published ?? true)
                        ? translate('project.visibleToUsers')
                        : translate('project.hiddenFromUsers')}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <ApplicationButton
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                  size="large"
                >
                  {translate('common.cancel')}
                </ApplicationButton>
                <ApplicationButton
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  size="large"
                  sx={{
                    minWidth: 150,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
                      : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                    boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': { boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.4)}` },
                  }}
                >
                  {saving ? translate('common.loading') : translate('common.save')}
                </ApplicationButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <UploadAvatarDialog
        key={uploadDialogKey}
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onImageUploaded={handleImageUploaded}
        currentImageUrl={null}
        storageRef={currentStorageRef}
        title={translate('project.addImage')}
        aspectRatio={16 / 9}
        cropShape="rect"
        maxWidth={1200}
        quality={0.85}
      />
    </>
  );
};

export default UpsertProject;
